import React, { useEffect, useState, useCallback } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import {
  Card,
  Statistic,
  Table,
  Button,
  Modal,
  Form,
  InputNumber,
  Input,
  Radio,
  Space,
  Row,
  Col,
  message,
} from 'antd';
import { DollarOutlined, PlusOutlined } from '@ant-design/icons';
import axios from 'axios';
import Layout from '../../layout/Layout';
import Header from '../../components/Header';
import { getHeaders } from '../../utility/getHeader';

// Only this one login username may see/top up the shared AI credit balance -
// mirrors aiReportsController.js's CREDIT_MANAGER_UNAME/isCreditManager.
// Gated on uname, not the numeric userId - a userId is an opaque database id
// invisible to whoever's actually using the app, which caused real confusion
// once already. This is purely a UI-visibility gate; the real enforcement is
// already server-side, so forcing this client-side would still hit a 403.
const CREDIT_MANAGER_UNAME = '3130';

export default function UsageBilling() {
  const [days, setDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [summary, setSummary] = useState(null);

  const [isCreditManager] = useState(
    () => (localStorage.getItem('uname') || sessionStorage.getItem('uname')) === CREDIT_MANAGER_UNAME
  );
  const [creditLoading, setCreditLoading] = useState(false);
  const [credit, setCredit] = useState(null);
  const [showAddCredit, setShowAddCredit] = useState(false);
  const [addCreditForm] = Form.useForm();
  const [addingCredit, setAddingCredit] = useState(false);

  const fetchSummary = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get('/ai-reports/usage/summary', {
        ...getHeaders(),
        params: { days },
      });
      setSummary(res.data?.data);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Could not load usage data. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [days]);

  const fetchCreditBalance = useCallback(async () => {
    if (!isCreditManager) return;
    setCreditLoading(true);
    try {
      const res = await axios.get('/ai-reports/credit/balance', getHeaders());
      setCredit(res.data?.data);
    } catch (err) {
      toast.error(err.response?.data?.msg || 'Could not load AI credit balance.');
    } finally {
      setCreditLoading(false);
    }
  }, [isCreditManager]);

  useEffect(() => {
    fetchSummary();
  }, [fetchSummary]);

  useEffect(() => {
    fetchCreditBalance();
  }, [fetchCreditBalance]);

  const handleAddCredit = async (values) => {
    setAddingCredit(true);
    try {
      const res = await axios.post(
        '/ai-reports/credit/topup',
        { amount_usd: values.amount_usd, note: values.note },
        getHeaders()
      );
      setCredit(res.data?.data);
      message.success('Credit added');
      setShowAddCredit(false);
      addCreditForm.resetFields();
    } catch (err) {
      message.error(err.response?.data?.msg || 'Could not add credit. Please try again.');
    } finally {
      setAddingCredit(false);
    }
  };

  const totals = summary?.totals;
  const byAdmin = summary?.by_admin || [];
  const daily = summary?.daily || [];

  const adminColumns = [
    { title: 'Admin', dataIndex: 'admin_name', key: 'admin_name' },
    { title: 'Questions', dataIndex: 'questions', key: 'questions', align: 'right' },
    {
      title: 'Billed (USD)',
      dataIndex: 'billed_usd',
      key: 'billed_usd',
      align: 'right',
      render: (value) => `$${Number(value).toFixed(2)}`,
    },
  ];

  const dailyColumns = [
    { title: 'Date', dataIndex: 'date', key: 'date' },
    { title: 'Questions', dataIndex: 'questions', key: 'questions', align: 'right' },
    {
      title: 'Billed (USD)',
      dataIndex: 'billed_usd',
      key: 'billed_usd',
      align: 'right',
      render: (value) => `$${Number(value).toFixed(2)}`,
    },
  ];

  return (
    <Layout>
      <Header title="Usage & Billing" />
      <ToastContainer />

      {isCreditManager && (
        <Card
          title={
            <Space>
              <DollarOutlined />
              <span>AI Credit Balance</span>
            </Space>
          }
          extra={
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setShowAddCredit(true)}>
              Add Credit
            </Button>
          }
          loading={creditLoading}
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            <Col span={8}>
              <Statistic
                title="Current Balance"
                value={credit?.balance_usd ?? 0}
                precision={2}
                prefix="$"
                valueStyle={credit && credit.balance_usd <= 0 ? { color: '#cf1322' } : undefined}
              />
            </Col>
            <Col span={8}>
              <Statistic title="Total Topped Up" value={credit?.total_topped_up_usd ?? 0} precision={2} prefix="$" />
            </Col>
            <Col span={8}>
              <Statistic title="Total Spent" value={credit?.total_spent_usd ?? 0} precision={2} prefix="$" />
            </Col>
          </Row>
        </Card>
      )}

      <Card
        title="Claude Usage Summary"
        extra={
          <Radio.Group value={days} onChange={(e) => setDays(e.target.value)}>
            <Radio.Button value={7}>7 days</Radio.Button>
            <Radio.Button value={30}>30 days</Radio.Button>
            <Radio.Button value={90}>90 days</Radio.Button>
          </Radio.Group>
        }
        loading={loading}
        style={{ marginBottom: 16 }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Statistic title="Billed (you pay)" value={totals?.billed_usd ?? 0} precision={2} prefix="$" />
          </Col>
          <Col span={8}>
            <Statistic title="Questions asked" value={totals?.questions ?? 0} />
          </Col>
          <Col span={8}>
            <Statistic title="Tokens used" value={(totals?.input_tokens ?? 0) + (totals?.output_tokens ?? 0)} />
          </Col>
        </Row>
      </Card>

      <Card title="By Admin" style={{ marginBottom: 16 }}>
        <Table
          columns={adminColumns}
          dataSource={byAdmin}
          rowKey={(row) => row.admin_id ?? 'unknown'}
          pagination={false}
          size="small"
          locale={{ emptyText: 'No usage recorded in this period.' }}
        />
      </Card>

      <Card title="Daily Summary">
        <Table
          columns={dailyColumns}
          dataSource={daily}
          rowKey="date"
          pagination={false}
          size="small"
          locale={{ emptyText: 'No usage recorded in this period.' }}
        />
      </Card>

      <Modal
        title="Add AI Credit"
        open={showAddCredit}
        onCancel={() => setShowAddCredit(false)}
        onOk={() => addCreditForm.submit()}
        confirmLoading={addingCredit}
        okText="Add"
      >
        <Form form={addCreditForm} layout="vertical" onFinish={handleAddCredit}>
          <Form.Item
            label="Amount (USD)"
            name="amount_usd"
            rules={[{ required: true, message: 'Enter an amount greater than 0' }]}
          >
            <InputNumber min={0.01} step={1} precision={2} style={{ width: '100%' }} prefix="$" autoFocus />
          </Form.Item>
          <Form.Item label="Note (optional)" name="note">
            <Input placeholder="e.g. July top-up" />
          </Form.Item>
        </Form>
      </Modal>
    </Layout>
  );
}
